import React, { createContext, useContext, useState, useEffect } from "react";
import { createGlobalStyle, ThemeProvider as StyledThemeProvider } from "styled-components";
import axios from "axios";
import fallbackThemeData from "../utils/ThemeData"; 
import { config } from "../utils/config";

// ... [your utility functions here remain unchanged]

const ThemeStyledConfig = createGlobalStyle`
  :root {
    --primaryColor: ${(props) => props.theme.primaryColor};
    --secondary-textColor: ${(props) => props.theme.secondaryColor};
    --text-color: ${(props) => props.theme.textColor};
    --secondary-text-color: ${(props) => props.theme.secondaryTextColor};
    --hover-color: ${(props) => props.theme.hoverColor};
    --light-bg-color: ${(props) => props.theme.lightBgColor};
    --dark-bg-color: ${(props) => props.theme.darkBgColor};
    --light-border-color: ${(props) => props.theme.lightBorderColor};
  }
`;
// Darken a hex color
const darkenColor = (hex, amount = 0.1) => {
  if (!hex) return "#000000";
  let color = hex.replace("#", "");
  if (color.length === 3) color = color.split("").map((c) => c + c).join("");

  const num = parseInt(color, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.max(0, Math.floor(r * (1 - amount)));
  g = Math.max(0, Math.floor(g * (1 - amount)));
  b = Math.max(0, Math.floor(b * (1 - amount)));

  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const h = x.toString(16);
        return h.length === 1 ? "0" + h : h;
      })
      .join("")
  );
};

const lightenColor = (hex, amount = 0.2) => {
  if (!hex) return "#ffffff";
  let color = hex.replace("#", "");
  if (color.length === 3) color = color.split("").map((c) => c + c).join("");

  const num = parseInt(color, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.min(255, Math.floor(r + (255 - r) * amount));
  g = Math.min(255, Math.floor(g + (255 - g) * amount));
  b = Math.min(255, Math.floor(b + (255 - b) * amount));

  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const h = x.toString(16);
        return h.length === 1 ? "0" + h : h;
      })
      .join("")
  );
};

const getLuminance = (hex) => {
  let color = hex.replace("#", "");
  if (color.length === 3) color = color.split("").map((c) => c + c).join("");
  const r = parseInt(color.substring(0, 2), 16) / 255;
  const g = parseInt(color.substring(2, 4), 16) / 255;
  const b = parseInt(color.substring(4, 6), 16) / 255;

  const a = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  );

  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
};

// Make text very readable: 8x darker or lighter
const getPrimaryTextColor = (primaryColor) => {
  const luminance = getLuminance(primaryColor);
  if (luminance > 0.5) {
    // Light primary color → make text very dark
    return darkenColor(primaryColor, 0.875); // ~8x darker
  } else {
    // Dark primary color → make text very light
    return lightenColor(primaryColor, 0.875); // ~8x lighter
  }
};

const getSecondaryTextColor = (primaryColor) => {
  const luminance = getLuminance(primaryColor);
  if (luminance > 0.5) {
    // Light primary color → make text very dark
    return darkenColor(primaryColor, 0.575); // ~8x darker
  } else {
    // Dark primary color → make text very light
    return darkenColor(primaryColor, 0.875); // ~8x lighter
  }
};

const getTextColor = (primaryColor) => {
  // If primaryColor is explicitly white, use black text
  if (primaryColor === "#FFFFFF") {
    return "#000000";
  }
  // If primaryColor is explicitly black, use white text
  if (primaryColor === "#000000") {
    return "#FFFFFF";
  }
  const luminance = getLuminance(primaryColor);
  return luminance < 0.5 ? "#FFFFFF" : "#000000";
};

const ThemeContext = createContext({
  theme: null,
  loading: true,
  featureToggle: null,
  hostnameConfig: null,
});

const applyTheme = (branding = {}) => {
  const primaryColor = branding.primaryColor || "#1b3fcf";

  const textColor = getPrimaryTextColor(primaryColor);
  const secondaryTextColor = getSecondaryTextColor(primaryColor);
  const hoverColor = darkenColor(primaryColor, 0.1);
  const darkBgColor = darkenColor(primaryColor, 0.18);
  const lightBgColor = lightenColor(primaryColor, 0.25);
  const lightBorderColor = lightenColor(primaryColor, 0.55);

  return {
    logo: branding.logo || "",
    brandImage: branding.brandImage || "",
    favicon: branding.favicon || "",
    primaryColor,
    hoverColor,
    lightBgColor,
    darkBgColor,
    lightBorderColor,
    textColor,
    secondaryTextColor,
  };
};

const setFavicon = (href) => {
  if (!href) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
};

/**
 * The tenant config decides branding and feature toggles, but waiting for it
 * used to block the whole app behind a network round-trip (a blank screen on
 * every cold load). We now start from the last config this browser saw — or the
 * bundled default — and swap in the API's answer when it lands. The cache is
 * per hostname because each tenant has its own branding.
 */
const CONFIG_CACHE_KEY = `tenantConfig:${window.location.hostname}`;

const readCachedConfig = () => {
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedConfig = (apiConfig) => {
  try {
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(apiConfig));
  } catch {
    // Private mode or full storage — the app works without the cache.
  }
};

export const ThemeProvider = ({ children }) => {
  const initialConfig = readCachedConfig() || fallbackThemeData.config;

  const [theme, setTheme] = useState(() => applyTheme(initialConfig.branding || {}));
  const [featureToggle, setFeatureToggle] = useState(
    () => initialConfig.featureToggles || {}
  );
  const [hostnameConfig, setHostnameConfig] = useState({ isCommenterAI: false });

  useEffect(() => {
    const hostname = window.location.hostname;
    const host = { isCommenter: hostname === "commenter.ai" };
    setHostnameConfig(host);

    const loadTheme = async () => {
      try {
        const response = await axios.post(`${config.urlPlatform}/api/v2/auth/tenant-config`, {});
        const apiConfig = response?.data?.data?.config || {};

        const themeData = applyTheme(apiConfig.branding || {});
        setFavicon(themeData.favicon);

        setFeatureToggle(apiConfig.featureToggles || {});
        setTheme(themeData);
        writeCachedConfig(apiConfig);
      } catch (error) {
        console.error("Error loading theme from API. Falling back to default theme.", error);

        const fallbackBranding = fallbackThemeData.config.branding || {};
        const fallbackFeatureToggles = fallbackThemeData.config.featureToggles || {};

        const fallbackTheme = applyTheme(fallbackBranding);
        setFavicon(fallbackTheme.favicon);

        setTheme(fallbackTheme);
        setFeatureToggle(fallbackFeatureToggles);
      }
    };

    loadTheme();
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, hostnameConfig, featureToggle }}>
      <StyledThemeProvider theme={theme}>
        <ThemeStyledConfig />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
export default ThemeProvider;

// // Utility functions
// // For CSS file (global styles)
// const ThemeStyledConfig = createGlobalStyle`
//   :root {
//     --primaryColor: ${(props) => props.theme.primaryColor};
//     --primaryTextColor: ${(props) => props.theme.primaryTextColor};
//     --text-color: ${(props) => props.theme.textColor};
//     --secondaryTextColor: ${(props) => props.theme.secondaryTextColor};
//     --hover-color: ${(props) => props.theme.hoverColor};
//     --light-bg-color: ${(props) => props.theme.lightBgColor};
//     --dark-bg-color: ${(props) => props.theme.darkBgColor};
//     --light-border-color: ${(props) => props.theme.lightBorderColor};
//   }
// `;
// // Darken a hex color
// const darkenColor = (hex, amount = 0.1) => {
//   if (!hex) return "#000000";
//   let color = hex.replace("#", "");
//   if (color.length === 3) color = color.split("").map((c) => c + c).join("");
//   const num = parseInt(color, 16);
//   let r = (num >> 16) & 0xff;
//   let g = (num >> 8) & 0xff;
//   let b = num & 0xff;
//   r = Math.max(0, Math.floor(r * (1 - amount)));
//   g = Math.max(0, Math.floor(g * (1 - amount)));
//   b = Math.max(0, Math.floor(b * (1 - amount)));
//   return (
//     "#" +
//     [r, g, b]
//       .map((x) => {
//         const h = x.toString(16);
//         return h.length === 1 ? "0" + h : h;
//       })
//       .join("")
//   );
// };
// // Lighten a hex color
// const lightenColor = (hex, amount = 0.2) => {
//   if (!hex) return "#FFFFFF";
//   let color = hex.replace("#", "");
//   if (color.length === 3) color = color.split("").map((c) => c + c).join("");
//   const num = parseInt(color, 16);
//   let r = (num >> 16) & 0xff;
//   let g = (num >> 8) & 0xff;
//   let b = num & 0xff;
//   r = Math.min(255, Math.floor(r + (255 - r) * amount));
//   g = Math.min(255, Math.floor(g + (255 - g) * amount));
//   b = Math.min(255, Math.floor(b + (255 - b) * amount));
//   return (
//     "#" +
//     [r, g, b]
//       .map((x) => {
//         const h = x.toString(16);
//         return h.length === 1 ? "0" + h : h;
//       })
//       .join("")
//   );
// };
// // Calculate the luminance of the color
// const getLuminance = (hex) => {
//   let color = hex.replace("#", "");
//   if (color.length === 3) color = color.split("").map((c) => c + c).join("");
//   const r = parseInt(color.substring(0, 2), 16) / 255;
//   const g = parseInt(color.substring(2, 4), 16) / 255;
//   const b = parseInt(color.substring(4, 6), 16) / 255;
//   // Apply the luminance formula
//   const a = [r, g, b].map((v) => {
//     return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
//   });
//   // Return the luminance value
//   const luminance = 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
//   return luminance;
// };
// // Function to calculate PrimaryTextColor
// const getPrimaryTextColor = (primaryColor) => {
//   const luminance = getLuminance(primaryColor);
//   if (luminance > 0.5) {
//     // If primaryColor is light, make text 3x darker
//     return darkenColor(primaryColor, 0.65); // Adjust the factor for darkness (3x darker)
//   } else {
//     // If primaryColor is dark, lighten the text for readability
//     return lightenColor(primaryColor, 0.65); // Adjust the factor for lightness (3x lighter)
//   }
// };
// const getTextColor = (primaryColor) => {
//   // If primaryColor is explicitly white, use black text
//   if (primaryColor === "#FFFFFF") {
//     return "#000000";
//   }
//   // If primaryColor is explicitly black, use white text
//   if (primaryColor === "#000000") {
//     return "#FFFFFF";
//   }
//   const luminance = getLuminance(primaryColor);
//   return luminance < 0.5 ? "#FFFFFF" : "#000000";
// };
// // Theme Context setup
// const ThemeContext = createContext({ theme: null, loading: true, featureToggle: null, hostnameConfig: null });
// export const ThemeProvider = ({ children }) => {
//   const [theme, setTheme] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [featureToggle, setFeatureToggle] = useState(null);
//   const [hostnameConfig, setHostnameConfig] = useState({ isCommenterAI: false });
//   useEffect(() => {
//     const hostname = window.location.hostname;
//     const host = { isCommenter: hostname === "commenter.ai" };
//     setHostnameConfig(host);
//     const loadTheme = async () => {
//       try {
//         const branding = themeConfig.data?.config?.branding || {};
//         const primaryColor = branding.primaryColor || "#1B2DCF";
//         // Get text color based on luminance or specific color rules
//         const primaryTextColor = getTextColor(primaryColor);
//         const textColor = getPrimaryTextColor(primaryColor); // This is already defined logic to calculate textColor
//         const secondaryTextColor = textColor === "#FFFFFF" ? "#000000" : primaryColor;
//         // Derived colors
//         const hoverColor = darkenColor(primaryColor, 0.1);
//         const darkBgColor = darkenColor(primaryColor, 0.18);
//         const lightBgColor = lightenColor(primaryColor, 0.25);
//         const lightBorderColor = lightenColor(primaryColor, 0.55);
//         const themeData = {
//           logo: branding.logo || "",
//           brandImage: branding.brandImage || "",
//           favicon: branding.favicon || "",
//           primaryColor,
//           primaryTextColor,
//           hoverColor,
//           lightBgColor,
//           darkBgColor,
//           lightBorderColor,
//           textColor,
//           secondaryTextColor,
//         };
//         const featureToggles = themeConfig.data?.featureToggles || {};
//         // Apply favicon dynamically
//         if (themeData.favicon) {
//           let link = document.querySelector("link[rel~='icon']");
//           if (!link) {
//             link = document.createElement("link");
//             link.rel = "icon";
//             document.head.appendChild(link);
//           }
//           link.href = themeData.favicon;
//         }
//         setFeatureToggle(featureToggles);
//         setTheme(themeData);
//       } catch (error) {
//         console.error("Error loading theme:", error);
//       } finally {
//         setLoading(false);
//       }
//     };
//     loadTheme();
//   }, []);
//   if (loading) {
//     return (
//       <div
//         style={{
//           backgroundColor: "transparent",
//           height: "100vh",
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           color: "#fff",
//         }}
//       >
//         {/* Loading Indicator */}
//       </div>
//     );
//   }
//   return (
//     <ThemeContext.Provider value={{ theme, hostnameConfig, featureToggle }}>
//       <StyledThemeProvider theme={theme}>
//         <ThemeStyledConfig />
//         {children}
//       </StyledThemeProvider>
//     </ThemeContext.Provider>
//   );
// };
// export const useTheme = () => useContext(ThemeContext);
// export default ThemeProvider;




